import React, {useEffect} from 'react';
import {Dialog, AppBar, Toolbar, Typography, IconButton, useTheme} from '@mui/material';

import Icon from 'presentation/icons';
import {IFrameModalProps} from './types';

export function IFrameModal<TPostMessage = Record<string, unknown>>({
	setOpenIframe,
	openIframe,
	url,
	title,
	handleBack,
	allow,
	onRequestClose,
	onPostMessage,
}: IFrameModalProps<TPostMessage>) {
	const theme = useTheme();

	const handleClose = () => {
		if (onRequestClose) {
			onRequestClose();
			return;
		}
		setOpenIframe(false);
		window.history.back();
	};

	useEffect(() => {
		const handler = (
			ev: MessageEvent<{
				appointmentId: string;
				insuredId: number;
				messageType: string;
				userId: string;
			}>,
		) => {
			if (ev.data.messageType === 'onEnd') {
				setOpenIframe(false);
			}
		};

		window.addEventListener('message', handler);

		return () => window.removeEventListener('message', handler);
	}, [setOpenIframe]);

	useEffect(() => {
		if (!onPostMessage) return undefined;
		const handler = (ev: MessageEvent) => onPostMessage(ev.data as TPostMessage);
		window.addEventListener('message', handler);
		return () => window.removeEventListener('message', handler);
	}, [onPostMessage]);

	useEffect(() => {
		if (openIframe) {
			window.history.pushState({iframeOpen: true}, '');
		}

		const handlePopState = () => {
			if (openIframe) {
				setOpenIframe(false);
			}
		};

		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [openIframe]);

	return (
		<Dialog fullScreen open={openIframe} onClose={handleClose} sx={{zIndex: 9999}}>
			<AppBar
				sx={{
					position: 'relative',
				}}>
				<Toolbar
					variant="dense"
					sx={{
						backgroundColor: theme.palette.primary.main,
						color: theme.palette.primary.contrastText,
						height: '80px',
						gap: '8px',
					}}>
					{handleBack ? (
						<IconButton onClick={handleBack}>
							<Icon name="arrow-back" color={theme.palette.primary.contrastText} />
						</IconButton>
					) : null}
					<Typography
						sx={{
							flex: 1,
							fontSize: '24px',
							fontWeight: 400,
						}}>
						{title}
					</Typography>
					{!handleBack ? (
						<IconButton edge="start" onClick={handleClose} aria-label="close">
							<Icon name="close-filled" color={theme.palette.primary.contrastText} />
						</IconButton>
					) : null}
				</Toolbar>
			</AppBar>
			<iframe
				allow={allow ?? 'camera *;microphone *'}
				style={{border: 0}}
				src={url}
				width="100%"
				height="100%"
			/>
		</Dialog>
	);
}
